import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Sparkles, ArrowRight, Rocket, ChevronLeft, PlayCircle, Heart, Compass,
  Bot, Users, Send, FileText, Workflow, BookOpen, BriefcaseBusiness, Plug,
  GraduationCap, Phone, MessageSquare, ListChecks,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const LEGACY_KEY = "sas:onboarding_v1_done";
const OPEN_EVENT = "sas:open-welcome-tour";

/** Imperatively re-open the tour (for the "Take the tour again" menu item). */
export function openWelcomeTour() {
  try { window.dispatchEvent(new Event(OPEN_EVENT)); } catch (_) {}
}

/* ---------- video URL parsing ---------- */

function detectVideo(rawUrl) {
  if (!rawUrl || typeof rawUrl !== "string") return null;
  const url = rawUrl.trim();
  if (!url) return null;

  // YouTube
  const yt = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{6,})/);
  if (yt) return { kind: "youtube", embed: `https://www.youtube.com/embed/${yt[1]}?rel=0` };

  // Loom
  const lm = url.match(/loom\.com\/(?:share|embed)\/([A-Za-z0-9]{16,})/);
  if (lm) return { kind: "loom", embed: `https://www.loom.com/embed/${lm[1]}` };

  // Vimeo
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d{6,})/);
  if (vm) return { kind: "vimeo", embed: `https://player.vimeo.com/video/${vm[1]}` };

  // MP4 / WebM / MOV
  if (/\.(mp4|webm|mov|m4v)(\?.*)?$/i.test(url)) return { kind: "mp4", embed: url };

  return null;
}

function VideoEmbed({ url }) {
  const v = useMemo(() => detectVideo(url), [url]);
  if (!v) {
    return (
      <div
        className="relative flex aspect-video w-full items-center justify-center rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-fuchsia-500/10"
        data-testid="welcome-video-placeholder"
      >
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-white/5">
            <PlayCircle className="h-6 w-6 text-white/45" />
          </div>
          <div className="mt-3 text-sm text-white/55">Your welcome video will appear here</div>
          <div className="mt-1 text-xs text-white/35">
            Admins can set the URL in <span className="text-white/55">Admin → Platform</span>.
          </div>
        </div>
      </div>
    );
  }
  if (v.kind === "mp4") {
    return (
      <video
        controls
        playsInline
        className="aspect-video w-full rounded-2xl bg-black"
        src={v.embed}
        data-testid="welcome-video-mp4"
      />
    );
  }
  return (
    <iframe
      title="Welcome video"
      src={v.embed}
      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
      allowFullScreen
      className="aspect-video w-full rounded-2xl border border-white/10 bg-black"
      data-testid={`welcome-video-${v.kind}`}
    />
  );
}

/* ---------- 8-module grid ---------- */

const MODULE_TILES = [
  { slug: "chat",         name: "AI Chat",           icon: Bot,               accent: "text-indigo-300",  blurb: "Ask anything — powered by GPT-4o." },
  { slug: "leads",        name: "Smart Leads",       icon: Users,             accent: "text-violet-300",  blurb: "Import, score, and write openers." },
  { slug: "sequences",    name: "Sequences",         icon: Send,              accent: "text-fuchsia-300", blurb: "Multi-step email cadences." },
  { slug: "content",      name: "Content Engine",    icon: FileText,          accent: "text-pink-300",    blurb: "Posts, emails, blog drafts." },
  { slug: "agents",       name: "AI Agents",         icon: Workflow,          accent: "text-amber-300",   blurb: "A 24/7 site bot that captures leads." },
  { slug: "knowledge",    name: "Knowledge Base",    icon: BookOpen,          accent: "text-emerald-300", blurb: "Ground replies in your own docs." },
  { slug: "assistant",    name: "Executive Assistant", icon: BriefcaseBusiness, accent: "text-teal-300",  blurb: "Daily briefings + auto-tasks." },
  { slug: "integrations", name: "Integrations",      icon: Plug,              accent: "text-sky-300",     blurb: "CRM, email, comms, productivity." },
];

// Member-role variant — sales-flavoured blurbs, playbook tile up front.
const MEMBER_MODULE_TILES = [
  { slug: "playbook",     name: "Sales Playbook",    icon: GraduationCap,     accent: "text-indigo-300",  blurb: "Read this first — the pitch, the price, the objections." },
  { slug: "leads",        name: "Smart Leads",       icon: Users,             accent: "text-violet-300",  blurb: "Your pipeline. Log every call from the lead drawer." },
  { slug: "sequences",    name: "Sequences",         icon: Send,              accent: "text-fuchsia-300", blurb: "Three cadences are pre-loaded — review and activate." },
  { slug: "content",      name: "Content Engine",    icon: FileText,          accent: "text-pink-300",    blurb: "Cold emails, voicemails, LinkedIn DMs — saved templates." },
  { slug: "agents",       name: "Sales Coach (Sam)", icon: MessageSquare,     accent: "text-amber-300",   blurb: "Practice objections and pitch reviews — pre-trained on our KB." },
  { slug: "knowledge",    name: "Knowledge Base",    icon: BookOpen,          accent: "text-emerald-300", blurb: "Product overview, pricing, and objection handling — already there." },
  { slug: "chat",         name: "AI Chat",           icon: Bot,               accent: "text-sky-300",     blurb: "Quick questions, draft a reply, summarise a thread." },
  { slug: "leads",        name: "Log a Call",        icon: Phone,             accent: "text-teal-300",    blurb: "Capture every outcome inside the lead drawer." },
];

/* ---------- main component ---------- */

export function OnboardingWizard() {
  const { user, setUser, org } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [videoUrl, setVideoUrl] = useState("");
  const [saving, setSaving] = useState(false);
  const isMember = org?.role === "member";
  const tiles = isMember ? MEMBER_MODULE_TILES : MODULE_TILES;

  // Listen for imperative re-open
  useEffect(() => {
    const handler = () => { setStep(0); setOpen(true); };
    window.addEventListener(OPEN_EVENT, handler);
    return () => window.removeEventListener(OPEN_EVENT, handler);
  }, []);

  // Auto-show on first ever visit (after auth) + migrate legacy localStorage flag
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    (async () => {
      try {
        // If server already says complete → nothing to do
        if (user.has_completed_welcome_tour) {
          // Clean up legacy key if still hanging around
          try { localStorage.removeItem(LEGACY_KEY); } catch (_) {}
          return;
        }
        // Legacy migration: user from old build with localStorage flag set
        let legacy = false;
        try { legacy = localStorage.getItem(LEGACY_KEY) === "1"; } catch (_) {}
        if (legacy) {
          try {
            await api.post("/users/me/complete-welcome-tour");
            if (!cancelled) setUser({ ...user, has_completed_welcome_tour: true });
            try { localStorage.removeItem(LEGACY_KEY); } catch (_) {}
          } catch (_) {}
          return;
        }
        // Fresh user → show tour after a short delay so the shell can paint
        setTimeout(() => { if (!cancelled) setOpen(true); }, 700);
      } catch (_) {}
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.has_completed_welcome_tour]);

  // Fetch the platform welcome video URL whenever the modal opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/platform-settings/public");
        if (!cancelled) setVideoUrl(r.data?.welcome_video_url || "");
      } catch (_) {
        if (!cancelled) setVideoUrl("");
      }
    })();
    return () => { cancelled = true; };
  }, [open]);

  const close = async () => {
    setSaving(true);
    try {
      await api.post("/users/me/complete-welcome-tour");
      if (user) setUser({ ...user, has_completed_welcome_tour: true });
      try { localStorage.removeItem(LEGACY_KEY); } catch (_) {}
    } catch (_) { /* swallow — tour still closes */ }
    setSaving(false);
    setOpen(false);
  };

  // Slide 2 tile click: mark tour complete, close modal, and jump straight into the module.
  const selectModule = async (slug) => {
    setSaving(true);
    try {
      await api.post("/users/me/complete-welcome-tour");
      if (user) setUser({ ...user, has_completed_welcome_tour: true });
      try { localStorage.removeItem(LEGACY_KEY); } catch (_) {}
    } catch (_) { /* navigate regardless */ }
    setSaving(false);
    setOpen(false);
    navigate(`/app/${slug}`);
  };

  const firstName = user?.name?.split(" ")[0] || "friend";

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent
        className="max-w-2xl border-white/10 bg-[#0a0a0a] text-white sm:max-w-3xl"
        data-testid="onboarding-wizard"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-indigo-300">
            <Sparkles className="h-3 w-3" /> Welcome tour
          </div>
          <DialogTitle className="font-display text-3xl font-light tracking-tight" data-testid="welcome-title">
            {step === 0 && (isMember
              ? <>Welcome to the team, {firstName}.</>
              : <>Welcome to Symplee, {firstName}.</>)}
            {step === 1 && (isMember
              ? <>Your sales toolkit — start with the Playbook.</>
              : <>Eight tools — included in your one simple subscription.</>)}
            {step === 2 && (isMember
              ? <>Your first hour, in three steps.</>
              : <>You're all set. Take your time.</>)}
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {step === 0 && (isMember
              ? "Two minutes of orientation, then jump into the Sales Playbook. Watch the welcome video first — your manager left it for you."
              : "Two minutes of welcome — nothing to install, nothing required. Watch the short video below to see how Symplee fits into your day.")}
            {step === 1 && (isMember
              ? "Tap any tile to jump straight in. Most reps start with the Playbook to learn the pitch and the price."
              : "Each one solves a small, real problem. Try just the one that's most useful to you this week.")}
            {step === 2 && (isMember
              ? "Knock these out today and you'll feel oriented by lunch."
              : "You can change anything later. Nothing is permanent. We're here when you need us.")}
          </DialogDescription>
        </DialogHeader>

        <AnimatePresence mode="wait">
          {/* SLIDE 1 — Welcome + video */}
          {step === 0 && (
            <motion.div
              key="s0"
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
              className="space-y-4"
              data-testid="welcome-slide-1"
            >
              <VideoEmbed url={videoUrl} />
              {isMember ? (
                <div
                  className="rounded-2xl border border-indigo-500/20 bg-gradient-to-br from-indigo-500/10 to-fuchsia-500/5 p-4 text-sm text-white/75"
                  data-testid="welcome-member-cta"
                >
                  <div className="flex items-start gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/10">
                      <GraduationCap className="h-4 w-4 text-indigo-300" />
                    </div>
                    <div className="flex-1">
                      <div className="text-white">Start with the Sales Playbook.</div>
                      <div className="mt-1 text-xs text-white/55">
                        Eight chapters covering the pitch, the price, the promises, and the most common objections.
                        Most reps read it in under an hour.
                      </div>
                      <Link
                        to="/app/playbook"
                        onClick={() => { /* keep tour open — slide 2 lets them tap a tile */ }}
                        className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200"
                        data-testid="welcome-member-playbook-link"
                      >
                        Open the Playbook in a new view → <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-2xl border border-white/5 bg-white/[0.02] p-4 text-sm text-white/65">
                  <span className="text-white">A quick note:</span> nothing here is locked in. Cancel
                  any time. No contracts, no upsells, no surprise bills. We'd rather earn the next
                  month than trap you in this one.
                </div>
              )}
            </motion.div>
          )}

          {/* SLIDE 2 — module tile grid (variant per role) */}
          {step === 1 && (
            <motion.div
              key="s1"
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
              data-testid="welcome-slide-2"
            >
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {tiles.map((t, i) => (
                  <motion.button
                    key={`${t.slug}-${i}`}
                    type="button"
                    onClick={() => selectModule(t.slug)}
                    disabled={saving}
                    initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: i * 0.04, duration: 0.3 }}
                    className="group cursor-pointer rounded-2xl border border-white/5 bg-white/[0.02] p-3 text-left transition hover:border-fuchsia-500/30 hover:bg-fuchsia-500/[0.04] focus:outline-none focus:ring-2 focus:ring-fuchsia-500/40 disabled:opacity-60"
                    data-testid={`welcome-module-${t.slug}-${i}`}
                    aria-label={`Open ${t.name}`}
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 transition group-hover:bg-white/10">
                        <t.icon className={`h-4 w-4 ${t.accent}`} />
                      </div>
                      <div className="font-display text-sm">{t.name}</div>
                      <ArrowRight className="ml-auto h-3.5 w-3.5 text-white/25 transition group-hover:translate-x-0.5 group-hover:text-white/70" />
                    </div>
                    <p className="mt-2 text-xs text-white/50">{t.blurb}</p>
                  </motion.button>
                ))}
              </div>
              <div className="mt-4 text-xs text-white/45">
                {isMember
                  ? <>The <span className="text-white/70">Playbook</span> is the fastest way to ramp — start there.</>
                  : <>Every module has its own <span className="text-white/70">Guide</span> tab — open it any time for plain-English instructions.</>}
              </div>
            </motion.div>
          )}

          {/* SLIDE 3 — Closing (variant per role) */}
          {step === 2 && (
            <motion.div
              key="s2"
              initial={{ opacity: 0, x: 16 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -16 }}
              transition={{ duration: 0.25 }}
              className="space-y-5"
              data-testid="welcome-slide-3"
            >
              {isMember ? (
                <>
                  <div
                    className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-fuchsia-500/10 p-6"
                    data-testid="welcome-member-first-hour"
                  >
                    <div className="flex items-center gap-2 text-eyebrow text-indigo-300">
                      <ListChecks className="h-3.5 w-3.5" /> Your first hour
                    </div>
                    <h3 className="font-display mt-2 text-2xl font-light text-white">
                      Three steps — not nine modules.
                    </h3>
                    <p className="mt-2 text-sm text-white/65">
                      You don't need to learn every tool today. Do these three things and you'll feel oriented before lunch.
                    </p>
                  </div>
                  <div className="grid gap-3 text-sm text-white/65 sm:grid-cols-3">
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="first-hour-step-1">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-indigo-500/15 text-xs text-indigo-200">1</span>
                        <GraduationCap className="h-4 w-4 text-indigo-300" />
                      </div>
                      <div className="mt-3 text-white">Read the Playbook</div>
                      <div className="mt-1 text-xs text-white/45">Sales tab — chapters 1, 2, 3. Roughly 25 minutes.</div>
                      <Link to="/app/playbook" className="mt-3 inline-flex items-center gap-1 text-xs text-indigo-300 hover:text-indigo-200" data-testid="first-hour-link-playbook">
                        Open Playbook <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="first-hour-step-2">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-violet-500/15 text-xs text-violet-200">2</span>
                        <Users className="h-4 w-4 text-violet-300" />
                      </div>
                      <div className="mt-3 text-white">Review your leads</div>
                      <div className="mt-1 text-xs text-white/45">Skim Smart Leads. Pick one to call — use the Log Call button.</div>
                      <Link to="/app/leads" className="mt-3 inline-flex items-center gap-1 text-xs text-violet-300 hover:text-violet-200" data-testid="first-hour-link-leads">
                        Open Smart Leads <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid="first-hour-step-3">
                      <div className="flex items-center gap-2">
                        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-fuchsia-500/15 text-xs text-fuchsia-200">3</span>
                        <MessageSquare className="h-4 w-4 text-fuchsia-300" />
                      </div>
                      <div className="mt-3 text-white">Talk to Sam</div>
                      <div className="mt-1 text-xs text-white/45">Practice one objection with the Sales Coach agent before your first real call.</div>
                      <Link to="/app/agents" className="mt-3 inline-flex items-center gap-1 text-xs text-fuchsia-300 hover:text-fuchsia-200" data-testid="first-hour-link-coach">
                        Open Sales Coach <ArrowRight className="h-3 w-3" />
                      </Link>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className="rounded-2xl border border-white/10 bg-gradient-to-br from-indigo-500/10 via-violet-500/5 to-fuchsia-500/10 p-6">
                    <div className="flex items-center gap-2 text-eyebrow text-fuchsia-300">
                      <Heart className="h-3.5 w-3.5" /> One last thing
                    </div>
                    <h3 className="font-display mt-2 text-2xl font-light text-white">
                      Start with one small win this week.
                    </h3>
                    <p className="mt-2 text-sm text-white/65">
                      Pick the module that solves a problem you had today. Drafting an email,
                      cleaning up a contact list, answering questions on your site — anything small
                      is fine. The point isn't to use everything; it's to save you an hour somewhere.
                    </p>
                  </div>
                  <div className="grid gap-3 text-sm text-white/65 sm:grid-cols-3">
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <Compass className="mb-2 h-4 w-4 text-indigo-300" />
                      <div className="text-white">Lost?</div>
                      <div className="text-xs text-white/45">Every page has a Guide tab on the right.</div>
                    </div>
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <Sparkles className="mb-2 h-4 w-4 text-fuchsia-300" />
                      <div className="text-white">Stuck?</div>
                      <div className="text-xs text-white/45">Email support@symplee.ai — a real person replies.</div>
                    </div>
                    <div className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                      <Rocket className="mb-2 h-4 w-4 text-emerald-300" />
                      <div className="text-white">Take it again?</div>
                      <div className="text-xs text-white/45">User menu → "Take the tour again".</div>
                    </div>
                  </div>
                </>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-4 flex items-center justify-between border-t border-white/5 pt-4">
          <div className="flex items-center gap-1" data-testid="welcome-progress">
            {[0, 1, 2].map((i) => (
              <span
                key={i}
                className={`h-1 w-6 rounded-full transition ${i === step ? "brand-bg" : "bg-white/10"}`}
              />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              onClick={close}
              disabled={saving}
              className="text-white/55 hover:text-white"
              data-testid="onboarding-skip"
            >
              Skip tour
            </Button>
            {step > 0 && (
              <Button
                variant="outline"
                onClick={() => setStep((s) => s - 1)}
                className="rounded-full border-white/15 bg-white/5 text-white hover:bg-white/10"
                data-testid="onboarding-back"
              >
                <ChevronLeft className="mr-1 h-3.5 w-3.5" /> Back
              </Button>
            )}
            {step < 2 ? (
              <Button
                onClick={() => setStep((s) => s + 1)}
                className="rounded-full bg-white text-black hover:bg-zinc-200"
                data-testid="onboarding-next"
              >
                Next <ArrowRight className="ml-1 h-3.5 w-3.5" />
              </Button>
            ) : (
              <Button
                asChild
                onClick={close}
                disabled={saving}
                className="rounded-full bg-white text-black hover:bg-zinc-200"
                data-testid="onboarding-done"
              >
                <Link to="/app">
                  <Rocket className="mr-1 h-3.5 w-3.5" /> Let's go
                </Link>
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
